import { PublicHeader } from '@/components/PublicHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi } from '@/lib/api/auth';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export function PasswordRecovery({ confirm = false }: { confirm?: boolean }) {
  const location = useLocation();
  // Keep the email credentials when the header's skip link changes the fragment.
  const [credentials] = useState(
    () => new URLSearchParams(location.hash.slice(1))
  );
  const uid = credentials.get('uid');
  const token = credentials.get('token');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const invalidLink = confirm && (!uid || !token);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (confirm && password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const result = confirm
        ? await authApi.confirmPasswordReset(uid!, token!, password)
        : await authApi.requestPasswordReset(email.trim());
      setMessage(result.message);
      setPassword('');
      setConfirmation('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="public-page flex min-h-svh flex-col bg-background text-foreground">
      <PublicHeader actionTo="/login" actionLabel="Sign in" />
      <main
        id="main-content"
        className="flex flex-1 items-center justify-center px-5 py-12 sm:px-8"
      >
        <div className="w-full max-w-sm space-y-6">
          <h1 className="font-serif text-4xl font-normal tracking-tight">
            {confirm ? 'Choose a new password.' : 'Forgot your password?'}
          </h1>
          {message ? (
            <p role="status" className="text-sm leading-6">
              {message}
            </p>
          ) : invalidLink ? (
            <p role="alert" className="text-sm text-destructive">
              This reset link is incomplete. Request a new link below.
            </p>
          ) : (
            <form
              onSubmit={handleSubmit}
              aria-label={confirm ? 'Reset password' : 'Request password reset'}
              aria-busy={loading}
              className="space-y-5"
            >
              {confirm ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Use at least 8 characters. Avoid common passwords and
                      personal details.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={confirmation}
                      onChange={event => setConfirmation(event.target.value)}
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm leading-6 text-muted-foreground">
                    Enter your account email and we’ll send you a link to reset
                    your password.
                  </p>
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                  />
                </div>
              )}
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading
                  ? 'Please wait…'
                  : confirm
                    ? 'Save new password'
                    : 'Send reset link'}
              </Button>
            </form>
          )}
          {confirm && !message && (
            <Link
              to="/forgot-password"
              className="block text-sm text-primary hover:underline"
            >
              Request a new reset link
            </Link>
          )}
          <Link
            to="/login"
            className="block text-sm text-primary hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </main>
    </div>
  );
}
